import { useClientPortalAPI } from "@certn/api";
import { Divider, Icon, Link, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import React, { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDebounce } from "usehooks-ts";

import { CaseCounts } from "../../../types";
import { queryParamsToCrossTabSearchCountApiParams } from "./crossTabSearchParams";
import { StatusSubset } from "../types";
import { buildCaseTabHref, getCasesBasePath } from "../utils";

const CASE_SEARCH_MIN_LENGTH = 3;

type CrossTabSearchMessageProps = {
  statusSubset: StatusSubset;
  params: Record<string, string | undefined>;
  isGroupOverview: boolean;
};

const TAB_LINKS = {
  active: { path: "", countKey: "active" as const },
  review: { path: "/review", countKey: "review" as const },
  done: { path: "/done", countKey: "complete" as const },
};

function getTabName(
  tab: keyof typeof TAB_LINKS,
  t: (key: string) => string
): string {
  switch (tab) {
    case "active":
      return t("Active");
    case "review":
      return t("Review");
    case "done":
      return t("Done");
    default: {
      const _exhaustive: never = tab;
      return _exhaustive;
    }
  }
}

type TabLinkResult = {
  tab: keyof typeof TAB_LINKS;
  href: string;
  text: string;
};

function isTabLinkResult(
  result: TabLinkResult | null
): result is TabLinkResult {
  return result !== null;
}

export function CrossTabSearchMessage({
  statusSubset,
  params,
  isGroupOverview,
}: CrossTabSearchMessageProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const casesBasePath = getCasesBasePath(pathname);
  const debouncedSearch = useDebounce(params.search, 300);
  const debouncedCountParams = useMemo(
    () => ({ ...params, search: debouncedSearch }),
    [params, debouncedSearch]
  );
  const shouldFetch =
    debouncedSearch !== undefined &&
    debouncedSearch.length >= CASE_SEARCH_MIN_LENGTH;

  const countsUrl =
    isGroupOverview && params.customer_group_id
      ? (`/api/internal/client-portal/case/counts/filtered/${params.customer_group_id}/` as const)
      : ("/api/internal/client-portal/case/counts/" as const);

  const { data } = useClientPortalAPI(
    shouldFetch ? countsUrl : null,
    shouldFetch
      ? queryParamsToCrossTabSearchCountApiParams(debouncedCountParams)
      : undefined
  );

  const counts = data as CaseCounts | undefined;

  if (!shouldFetch || !counts) {
    return null;
  }

  const results = (["active", "review", "done"] as const)
    .filter((tab) => tab !== statusSubset)
    .map((tab) => {
      const count = counts[TAB_LINKS[tab].countKey];
      if (!count) {
        return null;
      }

      return {
        tab,
        href: buildCaseTabHref(
          casesBasePath,
          TAB_LINKS[tab].path,
          searchParams
        ),
        text: t("{{resultCount}} in {{tabName}}", {
          resultCount: count,
          tabName: getTabName(tab, t),
        }),
      };
    })
    .filter(isTabLinkResult);

  if (results.length === 0) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="cross-tab-search-message">
      <Icon
        padding="space-05"
        name="travel_explore"
        type="standard"
        color="icon-accent-blue"
      />
      <Text color="text-weak">
        <Trans>More results found:</Trans>
      </Text>{" "}
      {results.map(({ tab, href, text }, index) => (
        <React.Fragment key={tab}>
          <Link
            href={href}
            textColor="text-weak"
            testID={`cross-tab-link-${tab}`}
          >
            {text}
          </Link>
          {index < results.length - 1 && (
            <Divider isVertical className={styles.divider} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    alignItems: "center",
    columnGap: "var(--space-3)",
    width: "100%",
    background: "var(--background-accent-blue-weakest)",
    padding: "var(--space-2)",
    borderRadius: "var(--border-radius-2)",
  }),
  divider: css({
    display: "list-item",
  }),
};
